/**
 * Cart App Compatibility Script
 * 
 * Este script intercepta las llamadas al Shopify Cart API hechas por apps de terceros
 * y dispara los eventos nativos del tema para actualizar el carrito automáticamente.
 * 
 * Problema: Apps de Shopify (Quick Buy, Product Options, etc.) usan sus propios
 * formularios y no disparan los eventos CartAddEvent del tema.
 * 
 * Solución: Interceptar fetch() y XMLHttpRequest para detectar llamadas a /cart/add.js
 * y disparar los eventos correspondientes.
 */

import { CartAddEvent, CartUpdateEvent } from '@theme/events';

(function() {
  'use strict';

  // Guardar referencias originales
  const originalFetch = window.fetch;
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  /**
   * Verifica si la URL es una llamada al Cart API de Shopify
   * @param {string} url - URL a verificar
   * @returns {object|null} - Objeto con tipo de operación o null
   */
  function isCartApiCall(url) {
    const urlString = url.toString().toLowerCase();
    
    if (urlString.includes('/cart/add.js') || urlString.includes('/cart/add')) {
      return { type: 'add' };
    }
    if (urlString.includes('/cart/change.js') || urlString.includes('/cart/change')) {
      return { type: 'change' };
    }
    if (urlString.includes('/cart/update.js') || urlString.includes('/cart/update')) {
      return { type: 'update' };
    }
    
    return null;
  }

  /**
   * Obtiene el conteo actual del carrito
   * @returns {Promise<number>}
   */
  async function getCurrentCartCount() {
    try {
      const response = await originalFetch('/cart.js');
      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      console.error('Error fetching cart count:', error);
      return 0;
    }
  }

  /**
   * Dispara el evento de actualización del carrito
   * @param {string} type - Tipo de operación ('add', 'change', 'update')
   * @param {object} data - Datos de la respuesta
   */
  async function triggerCartEvent(type, data) {
    const itemCount = await getCurrentCartCount();
    
    if (type === 'add') {
      // Disparar evento de agregar al carrito
      document.dispatchEvent(
        new CartAddEvent({}, 'app-compatibility', {
          source: 'third-party-app',
          itemCount: itemCount,
          didError: false
        })
      );
    } else {
      // Disparar evento de actualización del carrito
      document.dispatchEvent(
        new CartUpdateEvent({}, 'app-compatibility', {
          source: 'third-party-app',
          itemCount: itemCount
        })
      );
    }
  }

  /**
   * Interceptar fetch()
   */
  window.fetch = function(...args) {
    const [url, options] = args;
    const cartOperation = isCartApiCall(url);

    if (cartOperation) {
      return originalFetch.apply(this, args).then(async (response) => {
        // Clonar la respuesta para poder leerla sin consumirla
        const clonedResponse = response.clone();
        
        try {
          const data = await clonedResponse.json();
          
          // Solo disparar evento si la operación fue exitosa
          if (response.ok && !data.status) {
            await triggerCartEvent(cartOperation.type, data);
          }
        } catch (error) {
          // Si no se puede parsear como JSON, ignorar
        }
        
        return response;
      });
    }

    return originalFetch.apply(this, args);
  };

  /**
   * Interceptar XMLHttpRequest
   */
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._cartOperation = isCartApiCall(url);
    return originalXHROpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    if (this._cartOperation) {
      // Guardar el callback original
      const originalOnLoad = this.onload;
      const originalOnReadyStateChange = this.onreadystatechange;
      const cartOperation = this._cartOperation;

      // Interceptar onload
      this.onload = function(event) {
        if (this.status >= 200 && this.status < 300) {
          try {
            const data = JSON.parse(this.responseText);
            if (!data.status) {
              triggerCartEvent(cartOperation.type, data);
            }
          } catch (error) {
            // Ignorar errores de parseo
          }
        }
        
        if (originalOnLoad) {
          originalOnLoad.apply(this, [event]);
        }
      };

      // Interceptar onreadystatechange
      this.onreadystatechange = function(event) {
        if (this.readyState === 4 && this.status >= 200 && this.status < 300) {
          try {
            const data = JSON.parse(this.responseText);
            if (!data.status) {
              triggerCartEvent(cartOperation.type, data);
            }
          } catch (error) {
            // Ignorar errores de parseo
          }
        }
        
        if (originalOnReadyStateChange) {
          originalOnReadyStateChange.apply(this, [event]);
        }
      };
    }

    return originalXHRSend.apply(this, args);
  };

  console.log('Cart App Compatibility: Interceptores activados');
})();
